import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Statistic, Table, DatePicker, Radio, Alert, Space, Grid, Tooltip, Tag, message } from 'antd';
import { PlusSquareOutlined, MinusSquareOutlined, WarningOutlined } from '@ant-design/icons';
import { raporlamaAPI } from '../services/api';
import { formatPara, formatSayi } from '../utils/format';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const TOPLU_RENK = '#1677ff';
const PERAKENDE_RENK = '#722ed1';
const CIRO_RENK = '#3f8600';
const FIRE_RENK = '#cf1322';
const MALIYET_RENK = '#d46b08';

const adet = (v) => (v === null || v === undefined ? '-' : formatSayi(v, 0));
const para = (v) => (v === null || v === undefined ? '-' : formatPara(v));
const yuzde = (v) => (v === null || v === undefined ? '-' : `${formatSayi(v * 100, 1)}%`);

const UYARI_METNI = {
  maliyet_dusuk: 'Reçeteden gelen maliyet, menü fiyatının 1/10’undan DÜŞÜK',
  maliyet_yuksek: 'Reçeteden gelen maliyet, menü fiyatının YARISINDAN yüksek',
  recete_eksik: 'Menü kalemlerinden birinin aktif reçetesi yok — reçete maliyeti EKSİK',
  malzeme_fiyatsiz: 'Reçetede birim fiyatı girilmemiş malzeme var — reçete maliyeti DÜŞÜK çıkıyor',
  menu_yok: 'O güne günün menüsü girilmemiş — maliyet hesaplanamıyor',
  fiyat_yok: 'Menü kalemlerinin liste fiyatı eksik — maliyet değerlendirilemiyor',
};

const ACIKLAMA = [
  'Satış adetleri menü bazındadır: Toplu Satış = Sabit gelir tipli satışların menü adedi,',
  'Perakende Satış = diğer gelir tiplerinin menü adedi. Her iki ciro da KDV HARİÇ hesaplanır.',
  'Üretim, üretim kayıtlarından gelir; mutfakta gerçekte üretilenden düşük olabileceği için Fire negatife düşebilir.',
  'Maliyet reçetelerden türetilir. Reçete maliyeti güvenilmez bulunursa',
  'o gün için menü fiyatının 1/3’ü varsayılır ve satırda işaretlenir.',
  'Brüt Kar, FİRE SONRASI birim maliyete göre hesaplanır (üretilenin tamamının parası ödendiği için).',
  'Fire Sonrası Birim Maliyet sütunundaki etiket, raporun reçete maliyetiyle mi varsayımla mı devam ettiğini gösterir.',
  'Toplam sütunlarının yanındaki + simgesiyle kırılımları açıp kapatabilirsiniz.',
].join(' ');

function IncelemeRaporu() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [kirilim, setKirilim] = useState('gun');
  // Varsayılan: içinde bulunulan ay ve öncesi 1 ay
  const [aralik, setAralik] = useState([dayjs().subtract(1, 'month').startOf('month'), dayjs()]);
  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(false);
  // Toplam sütunlarının kırılımları — varsayılan kapalı, tablo okunabilir kalsın
  const [acik, setAcik] = useState({ satis: false, ciro: false, brFiyat: false, maliyet: false });

  // Aylık kırılımda aralık tam aylara yuvarlanır ki "ayın bütün günleri" toplanmış olsun
  const araligiNormalize = (d, kir) => (kir === 'ay'
    ? [d[0].startOf('month'), d[1].endOf('month')]
    : d);

  const fetchRapor = useCallback(async (bas, bit, kir) => {
    setLoading(true);
    try {
      const res = await raporlamaAPI.inceleme({
        baslangic: bas.format('YYYY-MM-DD'),
        bitis: bit.format('YYYY-MM-DD'),
        kirilim: kir,
      });
      setRapor(res.data);
    } catch (err) {
      message.error('İnceleme raporu yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const a = araligiNormalize(aralik, kirilim);
    fetchRapor(a[0], a[1], kirilim);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aralikDegisti = (d) => {
    if (!d || !d[0] || !d[1]) return;
    const a = araligiNormalize(d, kirilim);
    setAralik(a);
    fetchRapor(a[0], a[1], kirilim);
  };

  const kirilimDegisti = (e) => {
    const kir = e.target.value;
    setKirilim(kir);
    const a = araligiNormalize(aralik, kir);
    setAralik(a);
    fetchRapor(a[0], a[1], kir);
  };

  // kirilim: kullanıcının SEÇİMİ — Radio ve tarih seçicinin granülasyonunu
  //   anında değiştirir.
  // gorunenKirilim: YÜKLÜ VERİNİN kırılımı (arka uç yanıtında echo edilir) —
  //   tablo düzenini (kolonlar, rowKey, sayfalama) bu belirler. Seçim değişip
  //   veri henüz gelmediğinde eski satırların yeni kolon düzeniyle render
  //   edilmesini önler.
  const gorunenKirilim = rapor?.kirilim || kirilim;
  const gunluk = gorunenKirilim === 'gun';
  // Tarih seçicinin granülasyonu seçimi anında takip eder
  const secimGunluk = kirilim === 'gun';
  const t = rapor?.toplam;

  // Toplam sütunu başlığına daraltma/genişletme düğmesi
  const grupBasligi = (etiket, anahtar) => (
    <Space size={4} style={{ whiteSpace: 'nowrap' }}>
      <span>{etiket}</span>
      <Tooltip title={acik[anahtar] ? 'Kırılımı gizle' : 'Kırılımı göster'}>
        <span
          role="button"
          onClick={(e) => { e.stopPropagation(); setAcik(a => ({ ...a, [anahtar]: !a[anahtar] })); }}
          style={{ cursor: 'pointer', color: TOPLU_RENK }}
        >
          {acik[anahtar] ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
        </span>
      </Tooltip>
    </Space>
  );

  // Raporun hangi maliyetle devam ettiğini gösteren etiket.
  // Günlükte tek kaynak vardır; aylıkta günler karışık olabilir.
  const kaynakEtiketi = (r) => {
    if (r.brut_br_maliyet === null || r.brut_br_maliyet === undefined) return null;
    if (gunluk) {
      return r.maliyet_varsayilan
        ? <Tooltip title={`Reçete maliyeti güvenilmez bulundu (${para(r.brut_br_maliyet_recete)}); yerine menü fiyatının 1/3’ü kullanıldı`}>
            <Tag color="orange" style={{ margin: 0 }}>varsayım</Tag>
          </Tooltip>
        : <Tooltip title="Reçeteden hesaplanan maliyet kullanıldı">
            <Tag color="blue" style={{ margin: 0 }}>reçete</Tag>
          </Tooltip>;
    }
    const v = r.varsayilan_gun || 0;
    const g = r.gun_sayisi || 0;
    if (v === 0) return <Tooltip title="Tüm günlerde reçeteden hesaplanan maliyet kullanıldı"><Tag color="blue" style={{ margin: 0 }}>reçete</Tag></Tooltip>;
    if (v === g) return <Tooltip title="Tüm günlerde menü fiyatının 1/3’ü varsayıldı"><Tag color="orange" style={{ margin: 0 }}>varsayım</Tag></Tooltip>;
    return <Tooltip title={`${v} günde varsayım, ${g - v} günde reçete maliyeti kullanıldı`}><Tag color="gold" style={{ margin: 0 }}>karma</Tag></Tooltip>;
  };

  // Daralmış halde görünen tek maliyet sütunu; kaynak etiketi burada durur
  const fireSonrasiHucresi = (r) => {
    if (r.fire_sonrasi_br_maliyet === null || r.fire_sonrasi_br_maliyet === undefined) return '-';
    return (
      <Space size={4}>
        <span style={{ color: MALIYET_RENK }}>{para(r.fire_sonrasi_br_maliyet)}</span>
        {kaynakEtiketi(r)}
      </Space>
    );
  };

  // Kırılım açıldığında görünen ham birim maliyet
  const maliyetHucresi = (r) => {
    if (r.brut_br_maliyet === null || r.brut_br_maliyet === undefined) return '-';
    const govde = <span style={{ color: MALIYET_RENK }}>{para(r.brut_br_maliyet)}</span>;
    if (!r.maliyet_varsayilan) return govde;
    return (
      <Tooltip title={`Menü fiyatının 1/3’ü varsayıldı; reçetenin ham sonucu ${para(r.brut_br_maliyet_recete)}`}>
        {govde}
      </Tooltip>
    );
  };

  // Sütunlar ve TOPLAM satırı aynı listeden türetilir ki kırılım açılıp
  // kapandığında ikisi birbirinden kaymasın.
  const sutunlar = useMemo(() => {
    const L = [];

    // Tarih: günlükte gün, aylıkta ay
    L.push({
      key: 'tarih', title: 'Tarih', width: gunluk ? 112 : 100,
      fixed: isMobile ? undefined : 'left', hizala: 'left',
      hucre: (r) => (gunluk ? dayjs(r.tarih).format('DD.MM.YYYY') : dayjs(`${r.donem}-01`).format('MM.YYYY')),
      ozet: <b>TOPLAM</b>,
    });

    if (gunluk) {
      L.push({ key: 'gun_tipi', title: 'Gün Tipi', width: 100, hizala: 'left',
        hucre: (r) => r.gun_tipi, ozet: t ? <b>{t.gun_sayisi} gün</b> : null });
    } else {
      L.push({ key: 'gun_sayisi', title: 'Gün Sayısı', width: 100, hizala: 'right',
        hucre: (r) => adet(r.gun_sayisi), ozet: t ? <b>{adet(t.gun_sayisi)}</b> : null });
    }

    L.push({ key: 'uretim', title: 'Üretim', width: 88, hizala: 'right',
      hucre: (r) => adet(r.uretim), ozet: t ? <b>{adet(t.uretim)}</b> : null });

    // --- Satış grubu ---
    if (acik.satis) {
      L.push({ key: 'toplu_satis', title: 'Toplu Satış', width: 100, hizala: 'right',
        hucre: (r) => <span style={{ color: TOPLU_RENK, fontWeight: 600 }}>{adet(r.toplu_satis)}</span>,
        ozet: t ? <b style={{ color: TOPLU_RENK }}>{adet(t.toplu_satis)}</b> : null });
      L.push({ key: 'perakende_satis', title: 'Perakende Satış', width: 125, hizala: 'right',
        hucre: (r) => <span style={{ color: PERAKENDE_RENK }}>{adet(r.perakende_satis)}</span>,
        ozet: t ? <b style={{ color: PERAKENDE_RENK }}>{adet(t.perakende_satis)}</b> : null });
    }
    L.push({ key: 'toplam_satis', title: grupBasligi('Toplam Satış', 'satis'), width: 128, hizala: 'right',
      hucre: (r) => <b>{adet(r.toplam_satis)}</b>, ozet: t ? <b>{adet(t.toplam_satis)}</b> : null });

    L.push({ key: 'fire', title: 'Fire', width: 80, hizala: 'right',
      hucre: (r) => <span style={{ color: r.fire != null && r.fire < 0 ? FIRE_RENK : undefined }}>{adet(r.fire)}</span>,
      ozet: t ? <b>{adet(t.fire)}</b> : null });
    L.push({ key: 'fire_yuzde', title: 'Fire %', width: 84, hizala: 'right',
      hucre: (r) => <span style={{ color: r.fire_yuzde != null && r.fire_yuzde < 0 ? FIRE_RENK : undefined }}>{yuzde(r.fire_yuzde)}</span>,
      ozet: t ? <b>{yuzde(t.fire_yuzde)}</b> : null });

    // --- Ciro grubu ---
    if (acik.ciro) {
      L.push({ key: 'toplu_ciro', title: 'Toplu Ciro (KDV hariç)', width: 160, hizala: 'right',
        hucre: (r) => <span style={{ color: TOPLU_RENK }}>{para(r.toplu_ciro)}</span>,
        ozet: t ? <b style={{ color: TOPLU_RENK }}>{para(t.toplu_ciro)}</b> : null });
      L.push({ key: 'perakende_ciro', title: 'Perakende Ciro', width: 140, hizala: 'right',
        hucre: (r) => <span style={{ color: PERAKENDE_RENK }}>{para(r.perakende_ciro)}</span>,
        ozet: t ? <b style={{ color: PERAKENDE_RENK }}>{para(t.perakende_ciro)}</b> : null });
    }
    L.push({ key: 'toplam_ciro', title: grupBasligi('Toplam Ciro', 'ciro'), width: 158, hizala: 'right',
      hucre: (r) => <b style={{ color: CIRO_RENK }}>{para(r.toplam_ciro)}</b>,
      ozet: t ? <b style={{ color: CIRO_RENK }}>{para(t.toplam_ciro)}</b> : null });

    // --- Birim fiyat grubu ---
    if (acik.brFiyat) {
      L.push({ key: 'toplu_br_fiyat', title: 'Toplu Satış Br. Fiyat', width: 160, hizala: 'right',
        hucre: (r) => para(r.toplu_br_fiyat), ozet: t ? <b>{para(t.toplu_br_fiyat)}</b> : null });
      L.push({ key: 'perakende_br_fiyat', title: 'Perakende Satış Br. Fiyat', width: 185, hizala: 'right',
        hucre: (r) => para(r.perakende_br_fiyat), ozet: t ? <b>{para(t.perakende_br_fiyat)}</b> : null });
    }
    L.push({ key: 'toplam_br_fiyat', title: grupBasligi('Toplam Satış Br. Fiyat', 'brFiyat'), width: 185, hizala: 'right',
      hucre: (r) => <b>{para(r.toplam_br_fiyat)}</b>, ozet: t ? <b>{para(t.toplam_br_fiyat)}</b> : null });

    // --- Maliyet grubu: daraldığında yalnızca "Fire Sonrası Birim Maliyet" kalır ---
    if (acik.maliyet) {
      if (gunluk) {
        L.push({ key: 'menu_fiyat', title: 'Menü Fiyatı', width: 118, hizala: 'right',
          hucre: (r) => para(r.menu_fiyat), ozet: null });
        L.push({ key: 'brut_br_maliyet_recete', title: 'Reçete Maliyeti', width: 145, hizala: 'right',
          hucre: (r) => <span style={{ color: '#999' }}>{para(r.brut_br_maliyet_recete)}</span>, ozet: null });
      }
      L.push({ key: 'brut_br_maliyet', title: 'Birim Maliyet', width: 145, hizala: 'right',
        hucre: maliyetHucresi,
        ozet: t ? <b style={{ color: MALIYET_RENK }}>{para(t.brut_br_maliyet)}</b> : null });
      L.push({ key: 'firenin_maliyeti', title: 'Fire Maliyeti', width: 140, hizala: 'right',
        hucre: (r) => <span style={{ color: FIRE_RENK }}>{para(r.firenin_maliyeti)}</span>,
        ozet: t ? <b style={{ color: FIRE_RENK }}>{para(t.firenin_maliyeti)}</b> : null });
    }
    L.push({ key: 'fire_sonrasi_br_maliyet', title: grupBasligi('Fire Sonrası Birim Maliyet', 'maliyet'), width: 230, hizala: 'right',
      hucre: fireSonrasiHucresi,
      ozet: t ? <Space size={4}><b style={{ color: MALIYET_RENK }}>{para(t.fire_sonrasi_br_maliyet)}</b>{kaynakEtiketi(t)}</Space> : null });

    // Brüt Kar artık maliyet grubunun altında değil, bağımsız sütun
    L.push({ key: 'gun_brut_kar', title: 'Brüt Kar', width: 150, hizala: 'right',
      hucre: (r) => (r.gun_brut_kar === null || r.gun_brut_kar === undefined ? '-' :
        <b style={{ color: r.gun_brut_kar >= 0 ? CIRO_RENK : FIRE_RENK }}>{para(r.gun_brut_kar)}</b>),
      ozet: t && t.gun_brut_kar !== null && t.gun_brut_kar !== undefined
        ? <b style={{ color: t.gun_brut_kar >= 0 ? CIRO_RENK : FIRE_RENK }}>{para(t.gun_brut_kar)}</b> : null });

    // --- Uyarı ---
    L.push({
      key: 'uyari', title: 'Uyarı', width: 70, hizala: 'center',
      hucre: (r) => {
        if (!gunluk) return r.uyarili_gun ? <Tag color="warning">{r.uyarili_gun}</Tag> : null;
        const u = r.maliyet_uyarilari || [];
        if (!u.length) return null;
        const agir = u.includes('menu_yok') || u.includes('fiyat_yok');
        return (
          <Tooltip title={<div>{u.map(k => <div key={k}>• {UYARI_METNI[k] || k}</div>)}</div>}>
            <WarningOutlined style={{ color: agir ? FIRE_RENK : MALIYET_RENK, fontSize: 16 }} />
          </Tooltip>
        );
      },
      ozet: t ? <b>{t.uyarili_gun || 0}</b> : null,
    });

    return L;
  }, [gunluk, isMobile, acik, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(() => sutunlar.map(s => ({
    title: s.title,
    key: s.key,
    width: s.width,
    align: s.hizala === 'left' ? undefined : s.hizala,
    fixed: s.fixed,
    render: (_, r) => s.hucre(r),
  })), [sutunlar]);

  const uyariOzeti = rapor?.maliyet_uyari_ozeti || {};
  const uyariSatirlari = Object.entries(uyariOzeti).filter(([, v]) => v > 0);
  const varsayilanGun = t?.varsayilan_gun || 0;

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={ACIKLAMA} />

      {varsayilanGun > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {varsayilanGun} günde maliyet, reçete yerine menü fiyatının 1/3’ü varsayıldı.
              </div>
              {uyariSatirlari.map(([k, v]) => (
                <div key={k}>• {v} gün: {UYARI_METNI[k] || k}</div>
              ))}
              <div style={{ marginTop: 4, color: '#666' }}>
                Gerçek reçete maliyetini görmek için: reçetelerin porsiyon sayısını ve malzeme birim fiyatlarını düzeltin.
                Maliyet kırılımını açarak reçetenin ham sonucunu karşılaştırabilirsiniz.
              </div>
            </div>
          }
        />
      )}

      <Space wrap style={{ marginBottom: 16 }}>
        <Radio.Group value={kirilim} onChange={kirilimDegisti} optionType="button" buttonStyle="solid">
          <Radio.Button value="gun">Günlük</Radio.Button>
          <Radio.Button value="ay">Aylık</Radio.Button>
        </Radio.Group>
        <RangePicker
          picker={secimGunluk ? 'date' : 'month'}
          value={aralik}
          onChange={aralikDegisti}
          format={secimGunluk ? 'DD.MM.YYYY' : 'MM.YYYY'}
          allowClear={false}
        />
      </Space>

      {t && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8} md={4}>
            <Card size="small"><Statistic title="Gün" value={t.gun_sayisi} /></Card>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Card size="small"><Statistic title="Toplam Satış (menü)" value={t.toplam_satis} valueStyle={{ color: TOPLU_RENK }} /></Card>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Card size="small"><Statistic title="Toplam Ciro" value={t.toplam_ciro} precision={2} suffix="₺" valueStyle={{ color: CIRO_RENK, fontWeight: 700 }} /></Card>
          </Col>
          <Col xs={12} sm={12} md={5}>
            <Card size="small"><Statistic title="Brüt Kar" value={t.gun_brut_kar || 0} precision={2} suffix="₺" valueStyle={{ color: (t.gun_brut_kar || 0) >= 0 ? CIRO_RENK : FIRE_RENK, fontWeight: 700 }} /></Card>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Card size="small"><Statistic title="Ort. Menü Fiyatı" value={t.toplam_br_fiyat || 0} precision={2} suffix="₺" /></Card>
          </Col>
        </Row>
      )}

      {rapor?.uretim_kaydi_olmayan_gun > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`Seçili aralıkta ${rapor.uretim_kaydi_olmayan_gun} günün üretim kaydı yok; bu günler Üretim ve Fire hesabına girmiyor.`}
        />
      )}

      <Table
        columns={columns}
        dataSource={rapor?.satirlar || []}
        rowKey={(r) => r.tarih || r.donem}
        loading={loading}
        pagination={gunluk ? { defaultPageSize: 31, showSizeChanger: true, showTotal: (x, r) => `${r[0]}-${r[1]} / ${x} gün` } : false}
        size="small"
        bordered
        scroll={{ x: 'max-content' }}
        summary={() => (t ? (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ background: '#fafafa' }}>
              {sutunlar.map((s, i) => (
                <Table.Summary.Cell key={s.key} index={i} align={s.hizala === 'left' ? undefined : s.hizala}>
                  {s.ozet}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          </Table.Summary>
        ) : null)}
      />
    </div>
  );
}

export default IncelemeRaporu;
